import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { API_URL, ERRORS } from '#const';
import { baseApi } from '#store/apis/baseApi';
import { toast } from 'react-toastify';
import { Routes } from '#Routes';
import i18n from '#i18n/config';

// Configure axios CSRF settings
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';

const config = {
  headers: {
    'Content-Type': 'application/json',
    'X-CSRFToken':
      document.cookie
        .split('; ')
        .find((row) => row.startsWith('csrftoken'))
        ?.split('=')[1] || '',
  },
  withCredentials: true,
};

const parseErrors = ({ error, rejectWithValue }) => {
  if (error?.response?.status === 500) {
    return rejectWithValue({ error: ERRORS.SERVER_ERROR });
  } else if (error.response && error.response.data) {
    return rejectWithValue(error.response.data);
  } else {
    return rejectWithValue(error.message);
  }
};

export const authRegister = createAsyncThunk(
  'auth/register',
  async ({ email, password, language }, { rejectWithValue }) => {
    // Generate a random string of 8 characters
    const randomString = Math.random().toString(36).substring(2, 10);
    // Use Polish prefix if language is Polish, otherwise use English
    const prefix = language === 'pl' ? 'użytkownik-' : 'user-';
    const username = `${prefix}${randomString}`;

    try {
      const { data } = await axios.post(
        `${API_URL}/accounts/registration/`,
        {
          email,
          password1: password,
          password2: password,
          name: username,
          role: 'patient',
          language: language || 'pl',
        },
        config,
      );
      return data;
    } catch (error) {
      return parseErrors({ error, rejectWithValue });
    }
  },
);

export const authLogin = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}/accounts/login/`, { email, password }, config);
      return data;
    } catch (error) {
      return parseErrors({ error, rejectWithValue });
    }
  },
);

export const authLogout = createAsyncThunk(
  'auth/logout',
  async (_, { getState, dispatch }) => {
    try {
      const { userToken } = getState().auth;

      // reset baseApi state
      dispatch(baseApi.util.resetApiState());

      if (!userToken) return; // user is already logout

      toast.info(i18n.t('auth.logoutToast'));

      const { data } = await axios.post(
        `${API_URL}/accounts/logout/`,
        {},
        {
          ...config,
          headers: {
            ...config.headers,
            Authorization: `Bearer ${userToken}`,
          },
        },
      );
      // Navigate after successful logout
      window.location.href = Routes.Dashboard.path;
      return data;
    } catch (error) {
      // Navigate even if there's an error
      window.location.href = Routes.Dashboard.path;
      // do nothing, always logout even if server reject
    }
  },
  {
    condition: (_, { getState }) => {
      // prevent to dispatch multiple logout action
      return !getState().auth?.isLogoutPending;
    },
  },
);

export const authForgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async ({ email }, { rejectWithValue }) => {
    try {
      const currentLanguage = i18n.language || 'pl'; // defaults to Polish if not set
      const { data } = await axios.post(
        `${API_URL}/accounts/password/reset/`,
        {
          email,
          language: currentLanguage,
        },
        config,
      );
      return data;
    } catch (error) {
      return parseErrors({ error, rejectWithValue });
    }
  },
);

export const authForgotPasswordSet = createAsyncThunk(
  'auth/forgotPasswordSet',
  async ({ password, token, uid }, { rejectWithValue }) => {
    try {
      const currentLanguage = i18n.language || 'pl'; // defaults to Polish if not set
      const { data } = await axios.post(
        `${API_URL}/accounts/password/reset/confirm/`,
        {
          new_password1: password,
          new_password2: password,
          token,
          uid,
          language: currentLanguage,
        },
        config,
      );
      return data;
    } catch (error) {
      return parseErrors({ error, rejectWithValue });
    }
  },
);
